/**
 * Shared constants for the Lost in Space application.
 * This file should be kept in sync with shared/constants.py
 */

export enum PlayerRole {
  AGENCY = "Agency",
  SPACESHIP = "Spaceship",
}

export type Role = PlayerRole.AGENCY | PlayerRole.SPACESHIP;

export const PLAYER_ROLES = Object.values(PlayerRole) as Role[];

export function isValidRole(value: string): value is Role {
  return PLAYER_ROLES.includes(value as Role);
}

// Game constants (should match backend app/config.py)
export const DEFAULT_MAX_TURNS = 10;
export const MAX_MESSAGE_LENGTH = 70;
export const MAX_CHARS_PER_TURN = 200;
export const MAX_CHARS_SENT_PER_TURN = 200;
export const MAX_LANDING_SITE_POINTS = 7;

// Player level bounds (keep in sync with backend settings)
export const COMM_LEVEL_MIN = 1;
export const COMM_LEVEL_MAX = 4;
export const SCAN_LEVEL_MIN = 1;
export const SCAN_LEVEL_MAX = 3;

// Spaceship damage threshold
export const MAX_ALLOWED_SPACESHIP_DAMAGE = 3;
export const MIN_ALLOWED_SPACESHIP_DAMAGE = 0;

// Player actions constants
export const MAX_ACTIONS_PER_TURN = 2;

export enum GameStatus {
  PRE_GAME = "PRE-GAME",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
}

export type GameStatusType = GameStatus.PRE_GAME | GameStatus.ACTIVE | GameStatus.COMPLETED;

export const GAME_STATUSES = Object.values(GameStatus) as GameStatusType[];

export function isValidGameStatus(value: string): value is GameStatusType {
  return GAME_STATUSES.includes(value as GameStatusType);
}

export enum ActionStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  CANCELED = "CANCELED",
  ADMIN = "ADMIN",
}

export type ActionStatusType = ActionStatus.PENDING | ActionStatus.COMPLETED | ActionStatus.CANCELED | ActionStatus.ADMIN;

export const ACTION_STATUSES = Object.values(ActionStatus) as ActionStatusType[];

export function isValidActionStatus(value: string): value is ActionStatusType {
  return ACTION_STATUSES.includes(value as ActionStatusType);
}

export enum SpaceshipAction {
  MOVE = "Move",
  SCAN = "Scan",
  RAISE_SHIELDS = "Raise Shields",
  FIX_DAMAGE = "Fix Damage",
  ADAPT_LANDING_GEAR = "Adapt Landing Gear",
}

export type SpaceshipActionType =
  | SpaceshipAction.MOVE
  | SpaceshipAction.SCAN
  | SpaceshipAction.RAISE_SHIELDS
  | SpaceshipAction.FIX_DAMAGE
  | SpaceshipAction.ADAPT_LANDING_GEAR;

export const SPACESHIP_ACTIONS = Object.values(SpaceshipAction) as SpaceshipActionType[];

export function isValidSpaceshipAction(value: string): value is SpaceshipActionType {
  return SPACESHIP_ACTIONS.includes(value as SpaceshipActionType);
}

export const SPACESHIP_ACTION_COSTS: Record<SpaceshipAction, number> = {
  [SpaceshipAction.MOVE]: 1,
  [SpaceshipAction.SCAN]: 1,
  [SpaceshipAction.RAISE_SHIELDS]: 1,
  [SpaceshipAction.FIX_DAMAGE]: 2,
  [SpaceshipAction.ADAPT_LANDING_GEAR]: 1,
};

export const SPACESHIP_ACTION_INITIAL_STATUS: Record<SpaceshipAction, ActionStatus> = {
  [SpaceshipAction.MOVE]: ActionStatus.PENDING,
  [SpaceshipAction.SCAN]: ActionStatus.PENDING,
  [SpaceshipAction.RAISE_SHIELDS]: ActionStatus.COMPLETED,
  [SpaceshipAction.FIX_DAMAGE]: ActionStatus.COMPLETED,
  [SpaceshipAction.ADAPT_LANDING_GEAR]: ActionStatus.COMPLETED,
};

export const SPACESHIP_ACTION_ALLOWS_COLLABORATION: Record<SpaceshipAction, boolean> = {
  [SpaceshipAction.MOVE]: true,
  [SpaceshipAction.SCAN]: true,
  [SpaceshipAction.RAISE_SHIELDS]: false,
  [SpaceshipAction.FIX_DAMAGE]: false,
  [SpaceshipAction.ADAPT_LANDING_GEAR]: false,
};

export function getSpaceshipActionInitialStatus(action: SpaceshipAction, isAdmin: boolean = false): ActionStatus {
  // Spaceship actions don't currently support ADMIN status
  // If needed in the future, add them here similar to AgencyAction
  return SPACESHIP_ACTION_INITIAL_STATUS[action];
}

export function getSpaceshipActionCost(action: SpaceshipAction): number {
  return SPACESHIP_ACTION_COSTS[action];
}

export function allowsSpaceshipActionCollaboration(action: SpaceshipAction): boolean {
  return SPACESHIP_ACTION_ALLOWS_COLLABORATION[action];
}

export enum AgencyAction {
  SCAN = "Scan",
  IMPROVE_SCAN = "Improve Scan",
  IMPROVE_COMMUNICATION = "Improve Communication",
  BUILD_LANDING_SITE = "Build Landing Site",
}

export type AgencyActionType =
  | AgencyAction.SCAN
  | AgencyAction.IMPROVE_SCAN
  | AgencyAction.IMPROVE_COMMUNICATION
  | AgencyAction.BUILD_LANDING_SITE;

export const AGENCY_ACTIONS = Object.values(AgencyAction) as AgencyActionType[];

export function isValidAgencyAction(value: string): value is AgencyActionType {
  return AGENCY_ACTIONS.includes(value as AgencyActionType);
}

export const AGENCY_ACTION_COSTS: Record<AgencyAction, number> = {
  [AgencyAction.SCAN]: 1,
  [AgencyAction.IMPROVE_SCAN]: 1,
  [AgencyAction.IMPROVE_COMMUNICATION]: 1,
  [AgencyAction.BUILD_LANDING_SITE]: 1,
};

export const AGENCY_ACTION_INITIAL_STATUS: Record<AgencyAction, ActionStatus> = {
  [AgencyAction.SCAN]: ActionStatus.PENDING,
  [AgencyAction.IMPROVE_SCAN]: ActionStatus.COMPLETED,
  [AgencyAction.IMPROVE_COMMUNICATION]: ActionStatus.COMPLETED,
  [AgencyAction.BUILD_LANDING_SITE]: ActionStatus.COMPLETED,
};

export const AGENCY_ACTION_ALLOWS_COLLABORATION: Record<AgencyAction, boolean> = {
  [AgencyAction.SCAN]: false,
  [AgencyAction.IMPROVE_SCAN]: false,
  [AgencyAction.IMPROVE_COMMUNICATION]: false,
  [AgencyAction.BUILD_LANDING_SITE]: false,
};

export function getAgencyActionInitialStatus(action: AgencyAction, isAdmin: boolean = false): ActionStatus {
  // If admin is creating the action, return ADMIN status for supported actions
  if (isAdmin && action === AgencyAction.BUILD_LANDING_SITE) {
    return ActionStatus.ADMIN;
  }
  // Otherwise, return the default initial status
  return AGENCY_ACTION_INITIAL_STATUS[action];
}

export function getAgencyActionCost(action: AgencyAction): number {
  return AGENCY_ACTION_COSTS[action];
}

export function allowsAgencyActionCollaboration(action: AgencyAction): boolean {
  return AGENCY_ACTION_ALLOWS_COLLABORATION[action];
}

// Emoji mappings for actions (unique per action type)
export const ACTION_EMOJIS: Record<SpaceshipAction | AgencyAction, string> = {
  // Spaceship actions
  [SpaceshipAction.MOVE]: "🚀",
  [SpaceshipAction.SCAN]: "🔍",
  [SpaceshipAction.RAISE_SHIELDS]: "🛡️",
  [SpaceshipAction.FIX_DAMAGE]: "🔧",
  [SpaceshipAction.ADAPT_LANDING_GEAR]: "⚙️",
  // Agency actions
  [AgencyAction.SCAN]: "📡",
  [AgencyAction.IMPROVE_SCAN]: "📊",
  [AgencyAction.IMPROVE_COMMUNICATION]: "📢",
  [AgencyAction.BUILD_LANDING_SITE]: "🏗️",
};

export function getActionEmoji(action: SpaceshipAction | AgencyAction): string {
  return ACTION_EMOJIS[action] || "⚡";
}

